
import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { PipelineColumn as PipelineColumnType, Prospect } from '@/types/pipeline';
import PipelineCard from './PipelineCard';
import { ScrollArea } from '@/components/ui/scroll-area';

interface PipelineColumnProps {
  column: PipelineColumnType;
  onProspectClick: (prospect: Prospect) => void;
}

const PipelineColumn = ({ column, onProspectClick }: PipelineColumnProps) => {
  const { setNodeRef } = useDroppable({
    id: column.id,
    data: {
      type: 'column',
      columnId: column.id
    }
  });
  
  // Log when the column renders with new data
  React.useEffect(() => {
    console.log(`[PipelineColumn] ${column.title} rendered with ${column.prospects.length} prospects`);
  }, [column.title, column.prospects.length]);

  return (
    <div className="flex flex-col h-full min-w-[280px] max-w-[280px]">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-medium text-md">{column.title}</h3>
        <div className="bg-gray-100 dark:bg-gray-700 rounded-full px-2 py-0.5 text-xs">
          {column.prospects.length}
        </div>
      </div>
      
      <div
        ref={setNodeRef}
        className="flex-1 bg-gray-50/50 dark:bg-slate-900/50 rounded-lg border border-gray-200/50 dark:border-gray-700/50 h-[calc(100vh-280px)]"
        data-column-id={column.id} /* Adding data attribute for easier debugging */
      >
        <ScrollArea className="h-full p-2">
          <SortableContext
            items={column.prospects.map(prospect => prospect.id)}
            strategy={verticalListSortingStrategy}
          >
            {column.prospects.map((prospect) => (
              <PipelineCard
                key={prospect.id}
                prospect={prospect}
                onClick={onProspectClick}
                columnId={column.id} /* Pass column ID to card for context */
              />
            ))}
            
            {column.prospects.length === 0 && (
              <div className="flex flex-col items-center justify-center h-24 text-gray-400 dark:text-gray-500 text-sm border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-md">
                <p>Drop prospects here</p>
              </div>
            )}
          </SortableContext>
        </ScrollArea>
      </div>
    </div>
  );
};

export default PipelineColumn;
